import type {
  Aircraft,
  AvailabilityWindow,
  Booking,
  CertState,
  Lesson,
  ProgramLesson,
  Profile,
  ProgressRecord,
  School,
} from "./types";
import type { BoardLookups } from "../stores/board";

export const SEED_SCHOOL: School = {
  id: "seed-school",
  slug: "skyhawk-vobg",
  name: "Skyhawk Aviation Academy",
  icao: "VOBG",
  city: "Bengaluru, KA",
  approvalRef: "DGCA-approved FTO · CAR Section 7 Series I",
  established: 2011,
  isDemo: true,
};

const PROFILES: Profile[] = [
  { id: "u-aarav",  name: "Aarav Kapoor",   role: "Owner",             initials: "AK", color: "#0b3a82" },
  { id: "u-priya",  name: "Priya Iyer",     role: "Scheduler",         initials: "PI", color: "#1452b8" },
  { id: "u-neha",   name: "Capt. Neha Rao", role: "Flight Instructor", initials: "NR", color: "#0e7490" },
  { id: "u-vikram", name: "Vikram Menon",   role: "Ground Instructor", initials: "VM", color: "#7c2d12" },
  { id: "u-rohan",  name: "Rohan Sharma",   role: "Student",           initials: "RS", color: "#15803d" },
  { id: "u-ananya", name: "Ananya Desai",   role: "Student",           initials: "AD", color: "#7c3aed" },
  { id: "u-kabir",  name: "Kabir Bhatia",   role: "Student",           initials: "KB", color: "#b45309" },
];

const AIRCRAFT: Aircraft[] = [
  { id: "ac-saa", tail: "VT-SAA", model: "Cessna 172S",  icon: "172",  state: "available",  maintenanceDueDays: 28 },
  { id: "ac-sab", tail: "VT-SAB", model: "Piper PA-28",  icon: "PA28", state: "maintenance", maintenanceDueDays: 3 },
  { id: "ac-sac", tail: "VT-SAC", model: "Diamond DA40", icon: "DA40", state: "dispatched",  maintenanceDueDays: 45 },
];

const LESSONS: Lesson[] = [
  { id: "l-pre",  name: "Pre-solo maneuvers",      kind: "flight" },
  { id: "l-xc",   name: "Cross-country planning",  kind: "ground" },
  { id: "l-pat",  name: "Pattern refinement",      kind: "flight" },
  { id: "l-solo", name: "Local solo",              kind: "flight" },
];

const PPL_LESSONS = [
  "Introduction & taxi", "Basic maneuvers", "Slow flight & stalls", "Steep turns",
  "Ground reference", "Pattern intro", "Pattern refinement", "Pre-solo maneuvers",
  "First solo", "Local solo", "Solo practice 1", "Solo practice 2",
  "XC planning (ground)", "Dual XC short", "Dual XC long", "Solo XC short",
  "Night intro", "Night XC", "Instrument hood 1", "Instrument hood 2",
  "Stage check", "Skill test prep 1", "Skill test prep 2", "DGCA skill test",
];
const PROGRAM_ID = "p-ppl";
const PROGRAM_LESSONS: ProgramLesson[] = PPL_LESSONS.map((name, i) => ({
  programId: PROGRAM_ID, position: i + 1, name,
}));

const CERTS: Record<string, CertState> = {
  "u-rohan":  { medical: "missing", studentCert: "current" },
  "u-ananya": { medical: "current", studentCert: "current" },
  "u-kabir":  { medical: "current", studentCert: "current" },
  "u-neha":   { medical: "current", instructor: "current" },
  "u-vikram": { medical: "current", instructor: "current" },
};

const AVAILABILITY: Record<string, AvailabilityWindow[]> = {
  "u-neha":   [{ from: "13:00", to: "15:00", reason: "DGCA recurrent training" }],
  "u-vikram": [],
};

const PROGRESS: Record<string, ProgressRecord> = {
  "u-rohan": {
    program: "Private Pilot Licence (PPL)",
    completed: 7, total: 24,
    upcoming: "Pre-solo maneuvers", upcomingDate: "Today, 08:00",
    blockers: [{ kind: "compliance", text: "DGCA Class 2 medical certificate not on file", severity: "high" }],
    recommendation: "Upload Class 2 medical from DGCA-empanelled examiner before next solo booking.",
    recentGrades: [
      { lesson: "Pattern intro",     grade: "Satisfactory",  date: "Apr 22" },
      { lesson: "Ground reference",  grade: "Satisfactory",  date: "Apr 19" },
      { lesson: "Steep turns",       grade: "Needs review",  date: "Apr 15" },
    ],
    flightHours: { dual: 9.4, solo: 0, total: 9.4 },
  },
  "u-ananya": {
    program: "Private Pilot Licence (PPL)",
    completed: 14, total: 24,
    upcoming: "Cross-country planning", upcomingDate: "Tomorrow, 09:00",
    blockers: [],
    recommendation: "Schedule cross-country review with Vikram Menon this week.",
    recentGrades: [
      { lesson: "Solo practice 2", grade: "Satisfactory", date: "Apr 25" },
      { lesson: "Solo practice 1", grade: "Satisfactory", date: "Apr 23" },
      { lesson: "First solo",      grade: "Excellent",    date: "Apr 18" },
    ],
    flightHours: { dual: 14.2, solo: 4.6, total: 18.8 },
  },
  "u-kabir": {
    program: "Private Pilot Licence (PPL)",
    completed: 4, total: 24,
    upcoming: "Steep turns", upcomingDate: "Today, 12:00",
    blockers: [],
    recommendation: "Continue building confidence on slow flight before steep turns.",
    recentGrades: [
      { lesson: "Slow flight & stalls", grade: "Satisfactory", date: "Apr 24" },
      { lesson: "Basic maneuvers",      grade: "Satisfactory", date: "Apr 20" },
    ],
    flightHours: { dual: 4.8, solo: 0, total: 4.8 },
  },
};

const BOOKINGS: Booking[] = [
  { id: "b-1", lessonId: "l-pre",  studentId: "u-rohan",  instructorId: "u-neha",   aircraftId: "ac-saa", start: "08:00", end: "10:00", status: "scheduled",   ready: false, notes: "First slot of the day." },
  { id: "b-2", lessonId: "l-pat",  studentId: "u-ananya", instructorId: "u-neha",   aircraftId: "ac-sab", start: "10:30", end: "12:00", status: "scheduled",   ready: false, notes: "" },
  { id: "b-3", lessonId: "l-xc",   studentId: "u-kabir",  instructorId: "u-vikram", aircraftId: null,     start: "12:00", end: "13:00", status: "completed",   ready: false, notes: "Classroom, DGCA Air Nav syllabus." },
  { id: "b-4", lessonId: "l-solo", studentId: "u-rohan",  instructorId: null,       aircraftId: "ac-sac", start: "14:00", end: "15:30", status: "in-progress", ready: true,  notes: "Solo flight." },
];

export const SEED_LOOKUPS: BoardLookups = {
  aircraft: AIRCRAFT,
  lessons: LESSONS,
  profiles: PROFILES,
  certs: CERTS,
  availability: AVAILABILITY,
  progress: PROGRESS,
  programLessons: PROGRAM_LESSONS,
};

export const SEED_BOOKINGS: Booking[] = BOOKINGS;
